import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '../LoadingSpinner';

/**
 * Protected Route for Family Office Pages
 * 
 * Checks:
 * 1. User is authenticated
 * 2. User belongs to a Family Office
 * 3. FO account exists and is active
 */
export default function FOProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, error } = useAuth0();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkFOAccess = async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        setIsAuthorized(false);
        setChecking(false);
        return;
      }

      try {
        // Get access token
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
        });

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

        // Check if user belongs to a Family Office
        // We'll verify by trying to access the FO dashboard endpoint
        const foResponse = await fetch(`${API_BASE_URL}/api/fo/dashboard/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (foResponse.ok || foResponse.status === 200) {
          setIsAuthorized(true);
          setChecking(false);
          return;
        }

        // If we get a 403, it means user is authenticated but not authorized as FO
        if (foResponse.status === 403) {
          setIsAuthorized(false);
          setChecking(false);
          return;
        }

        // For other errors, deny access
        setIsAuthorized(false);
      } catch (error) {
        console.error('Error checking FO access:', error);
        setIsAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    checkFOAccess();
  }, [isAuthenticated, isLoading, user, getAccessTokenSilently]);

  if (isLoading || checking) {
    return <LoadingSpinner type="page" message="Verifying Family Office access..." />;
  }

  if (!isAuthenticated || !isAuthorized) {
    // Redirect to home or login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

